import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { adminCreateUser, validateUsername, validatePassword, validateEmail, userExists } from '../utils/userManager';
import { ROLES, hasPermission, PERMISSIONS } from '../utils/roleManager';

const { FiUpload, FiX, FiCheck, FiAlertTriangle, FiDownload, FiUsers, FiFile } = FiIcons;

const UserImportModal = ({ isOpen, onClose, onSuccess, currentUser }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Results
  const [importSettings, setImportSettings] = useState({
    skipFirstRow: true,
    defaultRole: ROLES.USER.id,
    updateExisting: false
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseFile(selectedFile);
    }
  };

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        let data = lines.map(line => {
          // Handle both CSV and TSV formats
          const cells = line.includes('\t') 
            ? line.split('\t') 
            : line.split(',').map(cell => cell.replace(/"/g, '').trim());
          
          return {
            username: cells[0]?.trim() || '',
            password: cells[1]?.trim() || '',
            email: cells[2]?.trim() || '',
            role: cells[3]?.trim() || importSettings.defaultRole
          };
        });

        // Skip first row if it contains headers
        if (importSettings.skipFirstRow && data.length > 0) {
          // Check if first row looks like headers
          const firstRow = data[0];
          if (firstRow.username?.toLowerCase().includes('username') || 
              firstRow.username?.toLowerCase().includes('name')) {
            data = data.slice(1);
          }
        }

        setCsvData(data);
        validateImportData(data);
        setStep(2);
      } catch (error) {
        alert('Error parsing file. Please ensure it\'s a valid CSV format.');
      }
    };
    reader.readAsText(file);
  };

  const validateImportData = (data) => {
    const results = data.map((row, index) => {
      const errors = [];
      const warnings = [];

      // Validate username
      if (!row.username) {
        errors.push('Username is required');
      } else {
        const usernameValidation = validateUsername(row.username);
        if (!usernameValidation.isValid) {
          errors.push(`Username: ${usernameValidation.errors[0]}`);
        }
        if (userExists(row.username)) {
          if (importSettings.updateExisting) {
            warnings.push('User exists - will be updated');
          } else {
            errors.push('Username already exists');
          }
        }
      }

      // Validate password
      if (!row.password) {
        errors.push('Password is required');
      } else {
        const passwordValidation = validatePassword(row.password);
        if (!passwordValidation.isValid) {
          errors.push(`Password: ${passwordValidation.errors[0]}`);
        }
      }

      // Validate email (optional)
      if (row.email) {
        const emailValidation = validateEmail(row.email);
        if (!emailValidation.isValid) {
          errors.push(`Email: ${emailValidation.errors[0]}`);
        }
      }

      // Validate role
      const roleExists = Object.values(ROLES).some(role => role.id === row.role);
      if (!roleExists) {
        warnings.push(`Invalid role "${row.role}", will use default`);
        row.role = importSettings.defaultRole;
      }

      return {
        index,
        row,
        errors,
        warnings,
        isValid: errors.length === 0
      };
    });

    setValidationResults(results);
  };

  const handleImport = async () => {
    if (!hasPermission(currentUser, PERMISSIONS.USER_MANAGEMENT)) {
      alert('You do not have permission to create users');
      return;
    }

    setIsProcessing(true);
    const results = {
      total: validationResults.length,
      success: 0,
      failed: 0,
      errors: []
    };

    for (const validation of validationResults) {
      if (validation.isValid) {
        try {
          const result = adminCreateUser(currentUser, {
            username: validation.row.username,
            password: validation.row.password,
            email: validation.row.email || null,
            role: validation.row.role
          });

          if (result.success) {
            results.success++;
          } else {
            results.failed++;
            results.errors.push({
              row: validation.index + 1,
              username: validation.row.username,
              error: result.error
            });
          }
        } catch (error) {
          results.failed++;
          results.errors.push({
            row: validation.index + 1,
            username: validation.row.username,
            error: 'Unexpected error occurred'
          });
        }
      } else {
        results.failed++;
        results.errors.push({
          row: validation.index + 1,
          username: validation.row.username,
          error: validation.errors.join(', ')
        });
      }
    }

    setImportResults(results);
    setIsProcessing(false);
    setStep(3);
    
    if (results.success > 0) {
      onSuccess(results);
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['Username', 'Password', 'Email', 'Role'],
      ['john_doe', 'password123', 'john@example.com', 'user'],
      ['jane_smith', 'securepass456', 'jane@example.com', 'premium_user'],
      ['admin_user', 'admin789', 'admin@example.com', 'admin']
    ];

    const csvContent = template.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'user_import_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const resetModal = () => {
    setFile(null);
    setCsvData([]);
    setValidationResults([]);
    setImportResults(null);
    setStep(1);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const getValidRowsCount = () => {
    return validationResults.filter(r => r.isValid).length;
  };

  const getAvailableRoles = () => {
    // Only show roles that the current user can assign
    return Object.values(ROLES).filter(role => {
      // Users can't assign roles equal to or higher than their own level
      // This would need to be implemented based on role hierarchy
      return true; // Simplified for now
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl shadow-2xl border border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiUpload} className="text-3xl text-blue-400" />
                <div>
                  <h2 className="text-2xl font-bold text-white">Import Users</h2>
                  <p className="text-gray-300">Bulk create users from CSV/Excel file</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                className="text-gray-400 hover:text-white"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </motion.button>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center justify-center mb-8">
              {[1, 2, 3].map((stepNumber) => (
                <div key={stepNumber} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= stepNumber 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-600 text-gray-300'
                  }`}>
                    {stepNumber}
                  </div>
                  {stepNumber < 3 && (
                    <div className={`w-12 h-0.5 mx-2 ${
                      step > stepNumber ? 'bg-blue-600' : 'bg-gray-600'
                    }`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step 1: File Upload */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Template Download */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-blue-300 font-semibold mb-1">Download Template</h3>
                      <p className="text-gray-300 text-sm">
                        Get a sample CSV file with the correct format
                      </p>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={downloadTemplate}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                    >
                      <SafeIcon icon={FiDownload} />
                      Template
                    </motion.button>
                  </div>
                </div>

                {/* Import Settings */}
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-4">Import Settings</h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={importSettings.skipFirstRow}
                        onChange={(e) => setImportSettings(prev => ({
                          ...prev,
                          skipFirstRow: e.target.checked
                        }))}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-white">Skip first row (headers)</span>
                    </label>

                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={importSettings.updateExisting}
                        onChange={(e) => setImportSettings(prev => ({
                          ...prev,
                          updateExisting: e.target.checked
                        }))}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-white">Update existing users</span>
                    </label>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Default Role
                      </label>
                      <select
                        value={importSettings.defaultRole}
                        onChange={(e) => setImportSettings(prev => ({
                          ...prev,
                          defaultRole: e.target.value
                        }))}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {getAvailableRoles().map(role => (
                          <option key={role.id} value={role.id} className="bg-slate-800">
                            {role.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* File Upload Area */}
                <div className="border-2 border-dashed border-white/20 rounded-lg p-8">
                  <div className="text-center">
                    <SafeIcon icon={FiFile} className="text-6xl text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Choose CSV or Excel file
                    </h3>
                    <p className="text-gray-300 mb-4">
                      File should contain: Username, Password, Email (optional), Role (optional)
                    </p>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors mx-auto"
                    >
                      <SafeIcon icon={FiUpload} />
                      Select File
                    </motion.button>
                    
                    {file && (
                      <p className="text-green-400 mt-4">
                        Selected: {file.name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Format Instructions */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <h4 className="text-yellow-300 font-semibold mb-2">File Format Requirements:</h4>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Column 1: Username (required)</li>
                    <li>• Column 2: Password (required)</li>
                    <li>• Column 3: Email (optional)</li>
                    <li>• Column 4: Role (optional, defaults to selected role)</li>
                    <li>• Supported formats: CSV, Excel (.xlsx, .xls)</li>
                    <li>• Maximum 1000 users per import</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Step 2: Preview and Validation */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Preview Import Data</h3>
                  <div className="text-sm text-gray-300">
                    {getValidRowsCount()} of {validationResults.length} rows valid
                  </div>
                </div>

                {/* Validation Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-400">{getValidRowsCount()}</div>
                    <div className="text-green-300 text-sm">Valid</div>
                  </div>
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-400">
                      {validationResults.length - getValidRowsCount()}
                    </div>
                    <div className="text-red-300 text-sm">Errors</div>
                  </div>
                  <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-400">
                      {validationResults.filter(r => r.warnings.length > 0).length}
                    </div>
                    <div className="text-yellow-300 text-sm">Warnings</div>
                  </div>
                </div>

                {/* Data Preview Table */}
                <div className="bg-white/5 rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-left">
                      <thead className="bg-white/10 sticky top-0">
                        <tr>
                          <th className="p-3 text-white font-semibold">Row</th>
                          <th className="p-3 text-white font-semibold">Username</th>
                          <th className="p-3 text-white font-semibold">Email</th>
                          <th className="p-3 text-white font-semibold">Role</th>
                          <th className="p-3 text-white font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResults.map((validation, index) => (
                          <tr key={index} className={`border-b border-white/10 ${
                            !validation.isValid ? 'bg-red-500/10' : ''
                          }`}>
                            <td className="p-3 text-gray-300">{validation.index + 1}</td>
                            <td className="p-3 text-white">{validation.row.username}</td>
                            <td className="p-3 text-gray-300">{validation.row.email || 'N/A'}</td>
                            <td className="p-3">
                              <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                                {ROLES[validation.row.role.toUpperCase()]?.name || validation.row.role}
                              </span>
                            </td>
                            <td className="p-3">
                              {validation.isValid ? (
                                <div className="flex items-center gap-1 text-green-400">
                                  <SafeIcon icon={FiCheck} />
                                  <span className="text-xs">Valid</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-red-400">
                                  <SafeIcon icon={FiAlertTriangle} />
                                  <span className="text-xs">Error</span>
                                </div>
                              )}
                              {validation.warnings.length > 0 && (
                                <div className="text-yellow-400 text-xs mt-1">
                                  {validation.warnings[0]}
                                </div>
                              )}
                              {validation.errors.length > 0 && (
                                <div className="text-red-400 text-xs mt-1">
                                  {validation.errors[0]}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setStep(1)}
                    className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Back
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleImport}
                    disabled={getValidRowsCount() === 0 || isProcessing}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-colors"
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Importing...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiUsers} />
                        Import {getValidRowsCount()} Users
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            )}

            {/* Step 3: Results */}
            {step === 3 && importResults && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">Import Complete</h3>
                </div>

                {/* Results Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-400">{importResults.total}</div>
                    <div className="text-blue-300 text-sm">Total Processed</div>
                  </div>
                  <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-400">{importResults.success}</div>
                    <div className="text-green-300 text-sm">Successful</div>
                  </div>
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-400">{importResults.failed}</div>
                    <div className="text-red-300 text-sm">Failed</div>
                  </div>
                </div>

                {/* Error Details */}
                {importResults.errors.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <h4 className="text-red-300 font-semibold mb-3">Import Errors:</h4>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {importResults.errors.map((error, index) => (
                        <div key={index} className="text-sm text-red-400">
                          Row {error.row} ({error.username}): {error.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {importResults.success > 0 && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                    <SafeIcon icon={FiCheck} className="text-4xl text-green-400 mx-auto mb-2" />
                    <p className="text-green-300 font-semibold">
                      Successfully imported {importResults.success} users!
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={resetModal}
                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Import More Users
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleClose}
                    className="flex-1 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UserImportModal;